import React from "react";
import api from "src/api";
import { getPoints, getProductGroups } from "src/utils";

const DEFAULT_COUNT_ON_PAGE = 30;

// AntD отдаёт в сортировке dataIndex колонки, а бэк ждёт имя поля из своего белого списка.
const SORT_FIELD_BY_COLUMN = {
  id: "id",
  nameRu: "name",
  sortWeight: "sortWeight",
};

export function useGetCategories() {
  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: DEFAULT_COUNT_ON_PAGE,
    total: 0,
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [sorting, setSorting] = React.useState(null);
  const [cityOptions, setCityOptions] = React.useState([]);
  const [categoryOptions, setCategoryOptions] = React.useState([]);

  // Страница, поиск и сортировка идут на бэк параметрами: категорий больше, чем влезает
  // в одну выдачу, и раньше таблица листала и сортировала только то, что уже приехало.
  async function getItems(
    page = 1,
    countOnPage = DEFAULT_COUNT_ON_PAGE,
    searchValue = "",
    sorting = null,
  ) {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page, countOnPage });
      if (searchValue) {
        params.append("search", searchValue);
      }
      if (sorting?.field && sorting?.order) {
        params.append("sortField", sorting.field);
        params.append("sortOrder", sorting.order);
      }

      const response = await api.get(`product-categories?${params.toString()}`);

      setPagination({
        current: response.data.pagination.currentPage,
        pageSize: response.data.pagination.countOnPage,
        total: response.data.pagination.total,
      });
      setItems(response.data.items);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }

  React.useEffect(() => {
    getItems();
    getPointsOptions();
    getCategoryOptions();
  }, []);

  // Сортировку отдаём на бэк: клиентская переставляла бы только текущие 30 строк
  // и врала бы про порядок — из-за неё и казалось, что пагинация выдаёт строки вразнобой.
  function handleTableChange(nextPagination, _filters, sorter) {
    const sortField = SORT_FIELD_BY_COLUMN[sorter?.field];
    const nextSorting = sorter?.order && sortField ? { field: sortField, order: sorter.order } : null;
    setSorting(nextSorting);
    getItems(nextPagination.current, nextPagination.pageSize, search, nextSorting);
  }

  function handleSearch(value) {
    const searchValue = value ?? "";
    setSearch(searchValue);
    // Всегда с первой страницы: на десятой странице результатов поиска обычно уже нет.
    getItems(1, pagination.pageSize, searchValue, sorting);
  }

  async function getPointsOptions() {
    const data = await getPoints();

    const options = data.map((item) => ({
      label: item.name,
      value: item.id,
    }));
    setCityOptions(options);
  }

  async function getCategoryOptions() {
    const data = await getProductGroups();

    const options = data.reduce((acc, item) => {
      const searchName = item?.name?.find((el) => el.language === "ru");
      return [
        ...acc,
        {
          label: searchName.content,
          value: item.id,
          disabled: !item.isActive,
        },
      ];
    }, []);
    setCategoryOptions(options);
  }

  return {
    items,
    pagination,
    isLoading,
    search,
    handleTableChange,
    handleSearch,
    refetch: () => getItems(pagination.current, pagination.pageSize, search, sorting),
    options: { cityOptions, categoryOptions },
  };
}

export function getFormattedItems(items, categoryOptions) {
  const data = items.map((item) => ({
    id: item.id,
    name: item.name,
    nameRu: item.name?.find((el) => el.language === "ru")?.content,
    isActive: item.isActive,
    isShowToMain: item.isShowToMain,
    sortWeight: item.sortWeight,
    imageUrl: item.imageUrl,
    hasOwnImage: Boolean(item.file),
    groups: item.groupProductCategoryIds
      .map((id) => categoryOptions?.find((option) => option.value === id))
      .filter(Boolean)
      .map((option) => ({
        label: option.label,
        disabled: option.disabled,
      })),
  }));

  return data;
}
